import {Color} from '@benchling/nucleus-ui';
import {Menu, Switch} from '@mantine/core';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/**
 * Combined "+" dropdown menu for file attachment, public data toggle, and MCP server selection.
 */
export default function PlusMenu({
  fileInputRef,
  agentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
}: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  agentType: AgentType | null;
  inputDisabled: boolean;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
}): JSX.Element {
  const isOpenAIEnabled = BenchlingConfig.LLM_ENABLE_OPENAI;
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const enabledServers = mcpEnabledServers?.enabledServers ?? [];

  const showPublicData = agentType != null && doesAgentTypeSupportPublicData(agentType);
  const showMcpSection =
    agentType != null && doesAgentTypeSupportSources(agentType) && setSelectedMcpServerIds != null;
  const hasMcpServers = showMcpSection && enabledServers.length > 0;

  // When undefined, all servers are selected
  const effectiveSelectedIds = selectedMcpServerIds ?? enabledServers.map((s) => s.id);

  const handleToggleServer = (serverId: string): void => {
    if (!setSelectedMcpServerIds) {
      return;
    }
    const isCurrentlySelected = effectiveSelectedIds.includes(serverId);
    if (isCurrentlySelected) {
      setSelectedMcpServerIds(effectiveSelectedIds.filter((id) => id !== serverId));
    } else {
      const newIds = [...effectiveSelectedIds, serverId];
      // If all servers are now selected, reset to undefined (= "all")
      if (newIds.length === enabledServers.length) {
        setSelectedMcpServerIds(undefined);
      } else {
        setSelectedMcpServerIds(newIds);
      }
    }
  };

  const handleAttachClick = useCallback((): void => {
    if (!inputDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [inputDisabled, fileInputRef]);

  const handleManageMCPs = useCallback((): void => {
    navigateToURL('/ai-connectors');
  }, []);

  return (
    <Menu
      position="bottom-start"
      withinPortal={true}
      width="max-content"
      styles={{
        item: {padding: 8, minHeight: 32, color: Color.GRAY_900},
        itemIcon: {color: Color.GRAY_600},
        itemRightSection: {marginLeft: 8},
        label: {color: Color.GRAY_600},
      }}
    >
      <Menu.Target>
        <span style={{display: 'inline-block'}}>
          <Button
            aria-label="More options"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={inputDisabled}
            icon="plus"
            size={ButtonSize.MINI}
          />
        </span>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item icon={<BenchlingIcon icon="paperclip" />} onClick={handleAttachClick}>
          Add photos and files
        </Menu.Item>
        {showPublicData && (
          <Menu.Item
            icon={<BenchlingIcon icon="globe" />}
            closeMenuOnClick={false}
            rightSection={
              <Switch
                checked={publicDataEnabled}
                disabled={!isOpenAIEnabled}
                onChange={() => {}}
                size="xs"
                styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
              />
            }
            onClick={() => {
              if (isOpenAIEnabled) {
                setPublicDataEnabled(!publicDataEnabled);
              }
            }}
          >
            Public data
          </Menu.Item>
        )}
        {(hasMcpServers || showMcpSection) && <Menu.Divider />}
        {hasMcpServers && (
          <>
            <Menu.Label>MCPs</Menu.Label>
            {enabledServers.map((server) => (
              <Menu.Item
                key={server.id}
                icon={<BenchlingIcon icon="sources" />}
                closeMenuOnClick={false}
                rightSection={
                  <Switch
                    checked={effectiveSelectedIds.includes(server.id)}
                    onChange={() => {}}
                    size="xs"
                    styles={{root: {pointerEvents: 'none'}, body: {margin: 0}}}
                  />
                }
                onClick={() => {
                  handleToggleServer(server.id);
                }}
              >
                {server.name}
              </Menu.Item>
            ))}
          </>
        )}
        {showMcpSection && (
          <Menu.Item icon={<BenchlingIcon icon="settings" />} onClick={handleManageMCPs}>
            Manage MCPs
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
